import React, { useEffect, useRef, useState, useCallback } from "react";
import "../App.css";
import {mobile} from "../services/mobileBridge";

const GRID = 20;
const CELL = 18;
const SIZE = GRID * CELL;
const TICK_MS = 110;

type Pt = { x: number; y: number };
type Dir = "up" | "down" | "left" | "right";

const DIRS: Record<Dir, Pt> = {
    up: { x: 0, y: -1 },
    down: { x: 0, y: 1 },
    left: { x: -1, y: 0 },
    right: { x: 1, y: 0 },
};

function randFood(snake: Pt[]): Pt {
    while (true) {
        const f = { x: Math.floor(Math.random() * GRID), y: Math.floor(Math.random() * GRID) };
        if (!snake.some(s => s.x === f.x && s.y === f.y)) return f;
    }
}

function NeonSnake({ country }: { country: 'south-africa' | 'nigeria' }) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [score, setScore] = useState(0);
    const [highScore, setHighScore] = useState(() => Number(localStorage.getItem("neonSnakeHi") || 0));
    const [started, setStarted] = useState(false);
    const [gameOver, setGameOver] = useState(false);

    const stateRef = useRef({
        snake: [{ x: 10, y: 10 }] as Pt[],
        dir: "right" as Dir,
        nextDir: "right" as Dir,
        food: { x: 5, y: 5 } as Pt,
        dead: false,
        started: false,
        lastTick: 0,
        flash: 0,
    });

    const animRef = useRef<number>(0);
    const touchStart = useRef<{ x: number; y: number } | null>(null);

    const setDir = useCallback((d: Dir) => {
        const s = stateRef.current;
        const cur = DIRS[s.dir];
        const next = DIRS[d];
        if (cur.x + next.x === 0 && cur.y + next.y === 0) return;
        s.nextDir = d;
    }, []);

    const reset = useCallback(() => {
        const s = stateRef.current;
        s.snake = [{ x: 10, y: 10 }, { x: 9, y: 10 }, { x: 8, y: 10 }];
        s.dir = "right";
        s.nextDir = "right";
        s.food = randFood(s.snake);
        s.dead = false;
        s.started = true;
        s.lastTick = performance.now();
        s.flash = 0;
        setScore(0);
        setGameOver(false);
        setStarted(true);
        if (animRef.current) cancelAnimationFrame(animRef.current);
        animRef.current = requestAnimationFrame(loop);
    }, []);

    const loop = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d", { alpha: false });
        if (!ctx) return;
        const s = stateRef.current;
        const now = performance.now();

        if (s.started && !s.dead && now - s.lastTick >= TICK_MS) {
            s.lastTick = now;
            s.dir = s.nextDir;
            const d = DIRS[s.dir];
            const head = s.snake[0];
            const nh = { x: head.x + d.x, y: head.y + d.y };

            if (nh.x < 0 || nh.x >= GRID || nh.y < 0 || nh.y >= GRID ||
                s.snake.some(p => p.x === nh.x && p.y === nh.y)) {
                s.dead = true;
                mobile.haptic("gameover");
                setGameOver(true);
                setScore(s.snake.length - 3);
                if (s.snake.length - 3 > highScore) {
                    setHighScore(s.snake.length - 3);
                    localStorage.setItem("neonSnakeHi", String(s.snake.length - 3));
                }
            } else {
                s.snake.unshift(nh);
                if (nh.x === s.food.x && nh.y === s.food.y) {
                    s.food = randFood(s.snake);
                    s.flash = 1;
                    setScore(s.snake.length - 3);
                } else {
                    s.snake.pop();
                }
            }
        }

        if (s.flash > 0) s.flash -= 0.05;

        // Draw
        const bg = ctx.createRadialGradient(SIZE / 2, SIZE / 2, 0, SIZE / 2, SIZE / 2, SIZE);
        bg.addColorStop(0, "#1a1a2e");
        bg.addColorStop(1, "#0f0f1e");
        ctx.fillStyle = bg;
        ctx.fillRect(0, 0, SIZE, SIZE);

        // Grid
        ctx.strokeStyle = "rgba(255,255,255,0.03)";
        ctx.lineWidth = 1;
        for (let i = 0; i <= GRID; i++) {
            ctx.beginPath();
            ctx.moveTo(i * CELL, 0);
            ctx.lineTo(i * CELL, SIZE);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(0, i * CELL);
            ctx.lineTo(SIZE, i * CELL);
            ctx.stroke();
        }

        // Food
        const fx = s.food.x * CELL + CELL / 2;
        const fy = s.food.y * CELL + CELL / 2;
        ctx.shadowBlur = 15;
        ctx.shadowColor = "#f43f5e";
        ctx.fillStyle = "#fb7185";
        ctx.beginPath();
        ctx.arc(fx, fy, CELL / 2 - 2, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
        if (s.flash > 0) {
            ctx.globalAlpha = s.flash;
            ctx.fillStyle = "#fff";
            ctx.beginPath();
            ctx.arc(fx, fy, CELL / 2, 0, Math.PI * 2);
            ctx.fill();
            ctx.globalAlpha = 1;
        }

        // Snake
        ctx.shadowBlur = 12;
        ctx.shadowColor = "#3ddbd9";
        for (let i = 0; i < s.snake.length; i++) {
            const seg = s.snake[i];
            const t = i / s.snake.length;
            const r = Math.floor(61 + t * 40);
            const g = Math.floor(219 - t * 60);
            const b = Math.floor(217 - t * 40);
            ctx.fillStyle = `rgb(${r},${g},${b})`;
            const px = seg.x * CELL + 1;
            const py = seg.y * CELL + 1;
            const sz = CELL - 2;
            const radius = 4;
            ctx.beginPath();
            ctx.roundRect(px, py, sz, sz, radius);
            ctx.fill();
        }
        ctx.shadowBlur = 0;

        // Eyes on head
        if (s.snake.length > 0) {
            const h = s.snake[0];
            const ex = h.x * CELL + CELL / 2;
            const ey = h.y * CELL + CELL / 2;
            ctx.fillStyle = "#1a1a2e";
            ctx.beginPath();
            ctx.arc(ex - 3, ey - 2, 2, 0, Math.PI * 2);
            ctx.arc(ex + 3, ey - 2, 2, 0, Math.PI * 2);
            ctx.fill();
        }

        animRef.current = requestAnimationFrame(loop);
    }, [highScore]);

    useEffect(() => {
        animRef.current = requestAnimationFrame(loop);
        return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
    }, [loop]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            switch (e.key) {
                case "ArrowUp": case "w": case "W": setDir("up"); e.preventDefault(); break;
                case "ArrowDown": case "s": case "S": setDir("down"); e.preventDefault(); break;
                case "ArrowLeft": case "a": case "A": setDir("left"); e.preventDefault(); break;
                case "ArrowRight": case "d": case "D": setDir("right"); e.preventDefault(); break;
                case " ":
                    e.preventDefault();
                    if (!stateRef.current.started) reset();
                    break;
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [setDir, reset]);

    const handleTouchStart = (e: React.TouchEvent) => {
        const t = e.touches[0];
        touchStart.current = { x: t.clientX, y: t.clientY };
    };
    const handleTouchEnd = (e: React.TouchEvent) => {
        if (!touchStart.current) return;
        const t = e.changedTouches[0];
        const dx = t.clientX - touchStart.current.x;
        const dy = t.clientY - touchStart.current.y;
        if (Math.abs(dx) < 20 && Math.abs(dy) < 20) {
            if (!stateRef.current.started) reset();
            return;
        }
        if (Math.abs(dx) > Math.abs(dy)) {
            setDir(dx > 0 ? "right" : "left");
        } else {
            setDir(dy > 0 ? "down" : "up");
        }
        touchStart.current = null;
    };

    return (
        <div className="container">
            <a href={`/game-studio/${country}`} className="game-back-link">&larr; Back to Games</a>
            <div className="score-display">Score: <span>{score}</span> &nbsp; Best: <span>{highScore}</span></div>
            <div style={{ position: "relative", display: "inline-block", borderRadius: 16, overflow: "hidden" }}>
                <canvas
                    ref={canvasRef}
                    width={SIZE}
                    height={SIZE}
                    className="game"
                    style={{ width: "100%", maxWidth: SIZE, touchAction: "none" }}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                    onClick={() => { if (!stateRef.current.started) reset(); }}
                />
                {!started && (
                    <div className="overlay">
                        <h2>🐍 Neon Snake</h2>
                        <p style={{ color: "var(--text-2)", fontSize: "0.9rem" }}>Swipe or use arrow keys</p>
                        <button className="center-btn" onClick={reset}>▶ Start</button>
                    </div>
                )}
                {gameOver && (
                    <div className="overlay">
                        <h2>💀 Score: {score}</h2>
                        {score >= highScore && score > 0 && <p style={{ color: "var(--good)" }}>New Best!</p>}
                        <button className="center-btn" onClick={reset}>🔄 Play Again</button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default NeonSnake;
